// DS: MOF3.1
import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import noop from '@wix/da-shared-react/pkg/utils/noop';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import { type SubNavigation } from '../../../../../types/store';
import { type ShopFilters } from '../../../../../types/shop';
import ShopFiltersPriceRadioGroup from './ShopFiltersPriceRadioGroup';
import ShopFiltersStatusRadioGroup from './ShopFiltersStatusRadioGroup';

import s from './ShopFiltersForm.scss';

export interface Props {
  searchPageType?: string;
  className?: string;
  filters?: any;
  priceRanges?: SubNavigation;
  saleTypes?: SubNavigation;
  status?: SubNavigation;
  updateFilters?: (update?: ShopFilters) => void;
  onApply?: (update?: ShopFilters) => void;
  resetFilters?: () => void;
}

type Status = ShopFilters['status'];

const ShopFiltersForm: React.FC<Props> = ({
  className,
  filters = {},
  priceRanges,
  status,
  updateFilters = noop,
  onApply = noop,
  resetFilters = noop,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  return (
    <form
      className={classnames(className, s['root'])}
      onSubmit={ev => ev.preventDefault()}
      onReset={ev => {
        ev.preventDefault();
        resetFilters();
      }}
    >
      {status && (
        <ShopFiltersStatusRadioGroup
          status={status}
          onChange={(_, value: Status) => updateFilters({ status: value })}
          currentFilterValue={filters.status}
          className={s['status']}
        />
      )}

      {!!priceRanges && (
        <ShopFiltersPriceRadioGroup
          className={s['price-ranges']}
          rangeClassName={s['price-range']}
          priceRanges={priceRanges}
          minPrice={filters.min_price}
          maxPrice={filters.max_price}
          setRange={(minPrice?: number, maxPrice?: number) =>
            updateFilters({ min_price: minPrice, max_price: maxPrice })
          }
        />
      )}

      {isMobile ? (
        <div className={s['mobile-button-group']}>
          <GhostButton
            type="reset"
            variant="main"
            size="large"
            onClick={resetFilters}
          >
            {t('shopPage.shopFiltersForm.resetButton.mobile')}
          </GhostButton>

          <SolidButton
            type="button"
            variant="approval-green"
            size="large"
            priority="primary"
            onClick={() => {
              // the user might press 'apply' when the tag is local to this form only
              // and hasn't been set in filters
              onApply();
              // onApply(tag ? { tag } : undefined);
              // setTag('');
            }}
          >
            {t('shopPage.shopFiltersForm.applyButton.mobile')}
          </SolidButton>
        </div>
      ) : (
        <TextButton
          className={s['reset-button-desktop']}
          type="reset"
          variant="main"
          size="small"
          onClick={resetFilters}
        >
          {t('shopPage.shopFiltersForm.resetButton')}
        </TextButton>
      )}
    </form>
  );
};

ShopFiltersForm.displayName = 'ShopFiltersForm';
export default ShopFiltersForm;
